import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Check,
  CircleAlert,
  ExternalLink,
  Github,
  MessageCircle,
  RotateCcw,
  Search,
  SearchX,
  ShoppingCart,
  Video,
} from 'lucide-react';
import apiClient from '../../api/apiClient';
import EmptyState from '../../components/site/EmptyState';
import PageHeader from '../../components/site/PageHeader';
import SEO from '../../components/SEO';
import VideoCard from '../../components/VideoCard';
import { productPath } from '../../components/productCard';
import { getWhatsAppUrl } from '../../configs/contactInfo';
import { useSiteConfig } from '../../configs/siteConfig';
import { seoConfig } from '../../configs/seoConfig';
import {
  formatDuration,
  formatTimestamp,
  getYouTubeVideoId,
} from '../../services/videoService';
import { Modal, Pagination } from '../../ui';
import { getErrorMessage } from '../../utils/apiError';
import { formatINR } from '../../utils/format';
import './VideosPage.css';

const PAGE_SIZE = 12;
const SEARCH_DELAY = 350;

// GET /ProjectVideo filters on the server (ProjectVideoQuery: Page, PageSize,
// SearchTerm, Technologies). Only published videos are returned to visitors.
const fetchVideos = async ({ page, search, tech, signal }) => {
  const response = await apiClient.get('/ProjectVideo', {
    params: {
      Page: page,
      PageSize: PAGE_SIZE,
      SearchTerm: search || undefined,
      Technologies: tech || undefined,
    },
    signal,
  });
  const data = response.data?.data || {};
  return {
    items: Array.isArray(data.items) ? data.items : [],
    total: Number(data.totalItems) || 0,
  };
};

const isCanceled = error => error?.code === 'ERR_CANCELED';

const cleanList = list => (Array.isArray(list) ? list.filter(Boolean) : []);

const SkeletonGrid = () => (
  <div className="pk-grid pk-grid--3" aria-hidden="true">
    {Array.from({ length: 6 }, (_, i) => (
      <div key={i} className="vd-skel">
        <span className="pk-skel vd-skel__media" />
        <span className="pk-skel vd-skel__line" />
        <span className="pk-skel vd-skel__line vd-skel__line--short" />
      </div>
    ))}
  </div>
);

const Checklist = ({ items, compact }) => (
  <ul className={`vd-checks${compact ? ' vd-checks--compact' : ''}`}>
    {items.map(item => (
      <li key={item}>
        <Check aria-hidden="true" />
        <span>{item}</span>
      </li>
    ))}
  </ul>
);

// "AI Resume Screening Tool – full demo" already names "AI Resume Screening Tool".
const titleNames = (title, name) => {
  const t = String(title || '').trim().toLowerCase();
  const n = String(name || '').trim().toLowerCase();
  return Boolean(n) && t.startsWith(n);
};

/**
 * The demo in a theater layout that always fits the window: the video on the
 * left (sized from the window), and a side panel with the price, Buy button and
 * tabbed details that scroll on their own. On phones it stacks full-screen.
 */
const VideoTheater = ({ video }) => {
  useSiteConfig();
  const youTubeId = getYouTubeVideoId(video.videoUrl);
  // Changing the key reloads the player at the chosen chapter.
  const [seek, setSeek] = useState({ start: null, key: 0 });

  const tech = cleanList(video.technologyStack);
  const features = cleanList(video.features);
  const included = cleanList(video.whatsIncluded);
  const chapters = youTubeId ? (video.chapters || []).filter(c => c?.title) : [];
  const product = video.product;
  const demoLink = video.projectLink?.trim();
  const githubLink = video.githubLink?.trim();
  const duration = formatDuration(video.duration);
  const facts = [video.category, video.level, duration].filter(Boolean);

  const tabs = [
    chapters.length > 0 && { id: 'chapters', label: 'Chapters' },
    (video.description || features.length > 0 || tech.length > 0) && {
      id: 'about',
      label: 'About',
    },
    (included.length > 0 || demoLink || githubLink) && {
      id: 'included',
      label: "What's included",
    },
  ].filter(Boolean);
  const [tab, setTab] = useState(tabs[0]?.id);
  const tabRefs = useRef({});

  const embedUrl = `https://www.youtube-nocookie.com/embed/${youTubeId}?autoplay=1&rel=0${
    seek.start ? `&start=${seek.start}` : ''
  }`;
  const playFrom = start => setSeek(prev => ({ start, key: prev.key + 1 }));

  const onTabKeyDown = (event, index) => {
    let next;
    if (event.key === 'ArrowRight') next = (index + 1) % tabs.length;
    else if (event.key === 'ArrowLeft') next = (index - 1 + tabs.length) % tabs.length;
    else return;
    event.preventDefault();
    setTab(tabs[next].id);
    tabRefs.current[tabs[next].id]?.focus();
  };

  return (
    <div className="vd-theater">
      <header className="vd-theater__head">
        <h2 className="vd-theater__title">{video.title}</h2>
        {facts.length > 0 && <p className="vd-theater__facts">{facts.join(' · ')}</p>}
      </header>

      <div className="vd-theater__stage">
        {youTubeId ? (
          <iframe
            key={seek.key}
            src={embedUrl}
            title={video.title}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        ) : (
          <a
            className="vd-theater__external"
            href={video.videoUrl}
            target="_blank"
            rel="noopener noreferrer"
          >
            <ExternalLink aria-hidden="true" />
            Watch the demo (opens in a new tab)
          </a>
        )}
      </div>

      <div className="vd-theater__side">
        {product && (
          <div className="vd-theater__buy">
            <div className="vd-theater__buy-row">
              <div className="vd-theater__buy-text">
                {!titleNames(video.title, product.name) && (
                  <span className="vd-theater__product">{product.name}</span>
                )}
                <span className="vd-theater__price">{formatINR(product.price)}</span>
              </div>
              <Link
                className="pk-btn pk-btn--primary pk-btn--sm"
                to={`/checkout/${product.id}`}
              >
                <ShoppingCart aria-hidden="true" />
                Buy now
              </Link>
            </div>
            <Link className="vd-theater__details" to={productPath(product)}>
              View project details
            </Link>
          </div>
        )}

        {tabs.length > 0 && (
          <>
            <div className="vd-tabs" role="tablist" aria-label="Demo details">
              {tabs.map((item, index) => (
                <button
                  key={item.id}
                  ref={el => {
                    tabRefs.current[item.id] = el;
                  }}
                  type="button"
                  role="tab"
                  id={`vd-tab-${item.id}`}
                  aria-selected={tab === item.id}
                  aria-controls={`vd-panel-${item.id}`}
                  tabIndex={tab === item.id ? 0 : -1}
                  className="vd-tab"
                  onClick={() => setTab(item.id)}
                  onKeyDown={e => onTabKeyDown(e, index)}
                >
                  {item.label}
                </button>
              ))}
            </div>

            <div
              className="vd-theater__panel"
              role="tabpanel"
              id={`vd-panel-${tab}`}
              aria-labelledby={`vd-tab-${tab}`}
              tabIndex={0}
            >
              {tab === 'chapters' && (
                <ol className="vd-chapters">
                  {chapters.map((chapter, i) => (
                    <li key={`${chapter.start}-${i}`}>
                      <button
                        type="button"
                        className="vd-chapter"
                        aria-current={seek.start === chapter.start ? 'true' : undefined}
                        onClick={() => playFrom(chapter.start)}
                      >
                        <span className="vd-chapter__time">
                          {formatTimestamp(chapter.start)}
                        </span>
                        <span>{chapter.title}</span>
                      </button>
                    </li>
                  ))}
                </ol>
              )}

              {tab === 'about' && (
                <div className="vd-panel-stack">
                  {video.description && (
                    <p className="vd-theater__desc">{video.description}</p>
                  )}
                  {features.length > 0 && (
                    <section>
                      <h3 className="vd-panel-title">Key features</h3>
                      <Checklist items={features} compact />
                    </section>
                  )}
                  {tech.length > 0 && (
                    <section>
                      <h3 className="vd-panel-title">Built with</h3>
                      <ul className="vd-tags" aria-label="Technologies">
                        {tech.map(item => (
                          <li key={item} className="pk-badge">
                            {item}
                          </li>
                        ))}
                      </ul>
                    </section>
                  )}
                </div>
              )}

              {tab === 'included' && (
                <div className="vd-panel-stack">
                  {included.length > 0 && <Checklist items={included} compact />}
                  {(demoLink || githubLink) && (
                    <div className="pk-cluster">
                      {demoLink &&
                        (demoLink.startsWith('/') ? (
                          <Link className="pk-btn pk-btn--secondary pk-btn--xs" to={demoLink}>
                            Live demo
                          </Link>
                        ) : (
                          <a
                            className="pk-btn pk-btn--secondary pk-btn--xs"
                            href={demoLink}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <ExternalLink aria-hidden="true" />
                            Live demo
                          </a>
                        ))}
                      {githubLink && (
                        <a
                          className="pk-btn pk-btn--secondary pk-btn--xs"
                          href={githubLink}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <Github aria-hidden="true" />
                          GitHub
                        </a>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          </>
        )}

        <p className="vd-help">
          <MessageCircle aria-hidden="true" />
          <span>
            Questions?{' '}
            <a
              className="pk-link"
              href={getWhatsAppUrl(
                `Hi Projects Kart, I watched the demo of "${video.title}" and have a question.`
              )}
              target="_blank"
              rel="noopener noreferrer"
            >
              Ask on WhatsApp
            </a>
          </span>
        </p>
      </div>
    </div>
  );
};

/** Same frame as the theater, so the dialog doesn't jump when the video arrives. */
const TheaterSkeleton = () => (
  <div className="vd-theater" aria-busy="true">
    <div className="vd-theater__head">
      <span className="pk-skel vd-skel__line" />
      <span className="pk-skel vd-skel__line vd-skel__line--short" />
    </div>
    <div className="vd-theater__stage" />
    <div className="vd-theater__side">
      <span className="pk-skel vd-skel__block" />
    </div>
  </div>
);

const VideosPage = () => {
  const [query, setQuery] = useState('');
  const [search, setSearch] = useState('');
  const [tech, setTech] = useState('');
  const [page, setPage] = useState(1);
  const [reloadKey, setReloadKey] = useState(0);
  const [result, setResult] = useState({
    status: 'loading',
    items: [],
    total: 0,
    error: '',
  });
  const [techOptions, setTechOptions] = useState([]);

  // The open video lives in the URL (?v=<id>), so a demo can be shared as a link.
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const openId = searchParams.get('v');
  const [preview, setPreview] = useState(null); // the card that was clicked, shown straight away
  const [detail, setDetail] = useState({ id: null, video: null, error: '' });
  const openedHere = useRef(false);

  const filtered = Boolean(search || tech);

  // Live search, debounced. Submitting the form searches straight away.
  useEffect(() => {
    const next = query.trim();
    if (next === search) return undefined;
    const timer = setTimeout(() => {
      setSearch(next);
      setPage(1);
    }, SEARCH_DELAY);
    return () => clearTimeout(timer);
  }, [query, search]);

  useEffect(() => {
    const controller = new AbortController();
    setResult(prev => ({ ...prev, status: 'loading', error: '' }));
    fetchVideos({ page, search, tech, signal: controller.signal })
      .then(({ items, total }) =>
        setResult({ status: 'ready', items, total, error: '' })
      )
      .catch(error => {
        if (isCanceled(error)) return;
        setResult({
          status: 'error',
          items: [],
          total: 0,
          error: getErrorMessage(error, "We couldn't load the videos."),
        });
      });
    return () => controller.abort();
  }, [page, search, tech, reloadKey]);

  // Technology filter options, from the published videos.
  useEffect(() => {
    const controller = new AbortController();
    apiClient
      .get('/ProjectVideo', {
        params: { Page: 1, PageSize: 100 },
        signal: controller.signal,
      })
      .then(response => {
        const items = response.data?.data?.items || [];
        const unique = new Set(
          items.flatMap(v => v.technologyStack || []).filter(Boolean)
        );
        setTechOptions([...unique].sort((a, b) => a.localeCompare(b)));
      })
      .catch(() => {
        // Without options the filter is simply hidden.
      });
    return () => controller.abort();
  }, []);

  // Load the open video by id: this also counts the view.
  useEffect(() => {
    if (!openId) {
      openedHere.current = false;
      return undefined;
    }
    const controller = new AbortController();
    apiClient
      .get(`/ProjectVideo/${encodeURIComponent(openId)}`, {
        signal: controller.signal,
      })
      .then(response =>
        setDetail({ id: openId, video: response.data?.data || null, error: '' })
      )
      .catch(error => {
        if (isCanceled(error)) return;
        setDetail({
          id: openId,
          video: null,
          error: getErrorMessage(error, "This video isn't available."),
        });
      });
    return () => controller.abort();
  }, [openId]);

  const openVideo = video => {
    openedHere.current = true;
    setPreview(video);
    setSearchParams(params => {
      const next = new URLSearchParams(params);
      next.set('v', video.id);
      return next;
    });
  };

  const closeVideo = () => {
    // Opened from this page: step back so the browser's Back button doesn't reopen it.
    if (openedHere.current) {
      openedHere.current = false;
      navigate(-1);
      return;
    }
    setSearchParams(
      params => {
        const next = new URLSearchParams(params);
        next.delete('v');
        return next;
      },
      { replace: true }
    );
  };

  const handleSubmit = e => {
    e.preventDefault();
    setSearch(query.trim());
    setPage(1);
  };

  const chooseTech = value => {
    setTech(value);
    setPage(1);
  };

  const clearFilters = () => {
    setQuery('');
    setSearch('');
    setTech('');
    setPage(1);
  };

  const retry = useCallback(() => setReloadKey(key => key + 1), []);

  const handlePageChange = next => {
    setPage(next);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // YouTube videos open the demo here; any other link opens in a new tab.
  const actionsFor = video => {
    if (getYouTubeVideoId(video.videoUrl)) {
      return { onClick: () => openVideo(video) };
    }
    const url = video.videoUrl?.trim();
    return /^https?:\/\//i.test(url || '') ? { href: url } : {};
  };

  const { status, items, total } = result;

  const fetched = detail.id === openId ? detail : null;
  const shownVideo =
    fetched?.video || (preview?.id === openId ? preview : null);

  let content;
  if (status === 'loading') {
    content = <SkeletonGrid />;
  } else if (status === 'error') {
    content = (
      <EmptyState
        role="alert"
        icon={<CircleAlert />}
        title="Couldn't load videos"
        text={result.error}
        actions={
          <button
            type="button"
            className="pk-btn pk-btn--secondary pk-btn--sm"
            onClick={retry}
          >
            <RotateCcw aria-hidden="true" />
            Retry
          </button>
        }
      />
    );
  } else if (items.length === 0) {
    content = filtered ? (
      <EmptyState
        icon={<SearchX />}
        title="No videos match"
        text="Try a different search or technology."
        actions={
          <button
            type="button"
            className="pk-btn pk-btn--secondary pk-btn--sm"
            onClick={clearFilters}
          >
            Clear filters
          </button>
        }
      />
    ) : (
      <EmptyState
        icon={<Video />}
        title="Walkthrough videos are coming soon"
        text="Meanwhile, each project page lists what's included and the tech stack."
        actions={
          <Link className="pk-btn pk-btn--primary pk-btn--sm" to="/products">
            Browse projects
          </Link>
        }
      />
    );
  } else {
    content = (
      <>
        <div className="pk-grid pk-grid--3">
          {items.map(video => (
            <VideoCard key={video.id} video={video} {...actionsFor(video)} />
          ))}
        </div>
        {total > PAGE_SIZE && (
          <div className="vd-pagination">
            <Pagination
              current={page}
              total={total}
              pageSize={PAGE_SIZE}
              onChange={handlePageChange}
            />
          </div>
        )}
      </>
    );
  }

  let modalBody = null;
  let theater = true;
  if (shownVideo) {
    modalBody = <VideoTheater key={shownVideo.id} video={shownVideo} />;
  } else if (fetched?.error) {
    theater = false;
    modalBody = (
      <EmptyState
        role="alert"
        icon={<CircleAlert />}
        title="Video not available"
        text={fetched.error}
      />
    );
  } else if (openId) {
    modalBody = <TheaterSkeleton />;
  }

  return (
    <main className="pk-page">
      <SEO {...seoConfig.pages.videos} />
      <div className="pk-container">
        <PageHeader
          title="Project videos"
          subtitle="Watch each project running before you buy. Every demo lists the features, what you get and the price."
          crumbs={[{ label: 'Home', to: '/' }, { label: 'Videos' }]}
        />

        <div className="vd-toolbar">
          <form className="vd-search" role="search" onSubmit={handleSubmit}>
            <label className="vd-sr" htmlFor="vd-search">
              Search videos
            </label>
            <div className="pk-input-icon vd-search__field">
              <Search aria-hidden="true" />
              <input
                id="vd-search"
                className="pk-input"
                type="search"
                placeholder="Search videos"
                value={query}
                onChange={e => setQuery(e.target.value)}
                autoComplete="off"
              />
            </div>
            <button type="submit" className="pk-btn pk-btn--secondary">
              Search
            </button>
          </form>

          {techOptions.length > 0 && (
            <div
              className="vd-chips"
              role="group"
              aria-label="Filter by technology"
            >
              <button
                type="button"
                className="pk-chip"
                aria-pressed={tech === ''}
                onClick={() => chooseTech('')}
              >
                All
              </button>
              {techOptions.map(option => (
                <button
                  key={option}
                  type="button"
                  className="pk-chip"
                  aria-pressed={tech === option}
                  onClick={() => chooseTech(tech === option ? '' : option)}
                >
                  {option}
                </button>
              ))}
            </div>
          )}
        </div>

        <p className="vd-count" aria-live="polite">
          {status === 'ready' && items.length > 0
            ? `${total} ${total === 1 ? 'video' : 'videos'}`
            : ''}
        </p>

        {content}
      </div>

      <Modal
        open={Boolean(openId)}
        onClose={closeVideo}
        title={shownVideo?.title || 'Project demo'}
        footer={null}
        width={theater ? undefined : 520}
        centered
        className={theater ? 'vd-modal vd-modal--theater' : 'vd-modal'}
        bodyClassName="vd-modal__body"
      >
        {modalBody}
      </Modal>
    </main>
  );
};

export default VideosPage;
